import { defineStore } from 'pinia';
import { last } from 'rxjs';
import type { ImpersonationSessionSummary, ImpersonationTargetSummary } from '@monolog/shared/types';
import { useToastStore } from '~/store/toast';

/**
 * Read-only engineer impersonation ("view as another user").
 *
 * Entering and leaving both do a HARD RELOAD rather than mutating state in place. That is deliberate: the reload is
 * what guarantees no engineer state survives the switch — the Pinia cache store, the merged user entitlements, the
 * persisted store selection, and the module-scoped realtime subscription would all otherwise leak across principals.
 * No secret is ever held here; the credential lives only in an HttpOnly cookie the browser sends automatically.
 */
export const useImpersonationStore = defineStore('impersonation', () => {
  const session = ref<ImpersonationSessionSummary | null>(null);
  /** True when the last bootstrap check could not be completed (transport/5xx), as opposed to confirmed-absent. */
  const indeterminate = ref(false);
  const targets = ref<ImpersonationTargetSummary[]>([]);
  const loading = ref(false);
  const starting = ref(false);

  const active = computed(() => !!session.value);

  /**
   * Whether to send the impersonation cookie. Deliberately wider than `active`: while the state is unknown the
   * cookie must still travel, or the client silently downgrades itself to the engineer's own session.
   */
  const shouldSendCredentials = computed(() => !!session.value || indeterminate.value);

  /** Resolve the current session on bootstrap so the banner survives the reload. Never throws. */
  function check(): Promise<void> {
    const { identity } = useGateway();

    return new Promise(resolve => {
      identity.impersonation
        .current()
        .pipe(last())
        .subscribe({
          error: () => {
            // A transport/5xx failure means UNKNOWN, not confirmed-absent — but only when a session is already
            // known. Clearing a known one would silently drop the engineer back into their own full-access
            // principal while the server session stays Active, so keep sending the cookie and let the server
            // decide. With nothing known there is nothing to preserve, and latching here would make every
            // later request credentialed: since this check runs for EVERY authenticated user on every load, a
            // CORS or gateway fault would then take the whole app down rather than just impersonation.
            indeterminate.value = !!session.value;
            resolve();
          },
          next: res => {
            indeterminate.value = false;
            session.value = res.data?.result === 'Active' ? res.data.session : null;
            resolve();
          },
        });
    });
  }

  function loadTargets(search?: string) {
    const { identity } = useGateway();
    loading.value = true;

    identity.impersonation
      .targets(search)
      .pipe(last())
      .subscribe({
        error: () => {
          targets.value = [];
          loading.value = false;
        },
        next: res => {
          targets.value = res.data ?? [];
          loading.value = false;
        },
      });
  }

  function start(targetUserId: string) {
    const { identity } = useGateway();
    starting.value = true;

    identity.impersonation
      .start(targetUserId)
      .pipe(last())
      .subscribe({
        error: error => {
          starting.value = false;
          useToastStore().addToast({
            message: error?.response?.data?.message ?? 'Could not start impersonation.',
            timeout: 5000,
            type: 'error',
          });
        },
        next: () => {
          // Full reload into the target's view — see the note above on why this is not a soft refresh.
          window.location.assign('/');
        },
      });
  }

  /** Exit back to the engineer's own session. Reloads to the root because the target's route may not exist for them. */
  function stop() {
    const { identity } = useGateway();

    identity.impersonation
      .stop()
      .pipe(last())
      .subscribe({
        error: () => {
          // The cookie may already be gone server-side; reload anyway so the UI cannot stay stuck in a dead session.
          window.location.assign('/');
        },
        next: () => {
          window.location.assign('/');
        },
      });
  }

  return {
    active,
    check,
    indeterminate,
    loadTargets,
    loading,
    session,
    shouldSendCredentials,
    start,
    starting,
    stop,
    targets,
  };
});
